// library imports
import { useEffect, useState,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import * as Constants from "_core/configs/Constants";

// User Component imports
import {Textarea, Row, Button, TextInput, Stack, Icon, Uploader, Card, Tab, LoadingBox} from "_core/components/common/UI";
import { DocsItem, InfoCard } from "components/common/parts";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";
import * as SceneConfig from "_core/configs/SceneConfig";

import AdminGetStructuredHtmlApi from "apis/AdminGetStructuredHtmlApi";
import AdminGetStructuredDocumentApi from "apis/AdminGetStructuredDocumentApi";

// styles imports
import './AdminUserRagStructure.scss';

const AdminUserRagStructure = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { tUser } = useSelector((store) => store.transaction);
    const { tAccount } = useSelector((store) => store.transaction);
    const { tAssetList } = useSelector((store) => store.transaction);
    const { initializeState } = useSelector((store) => store.scene);

    const [learningUrl, setLearningUrl] = useState('');
    const [htmlDownloadWaiting, setHTMLDownloadWaiting] = useState(false);
    const [documentDownloadWaiting, setDocumentDownloadWaiting] = useState(false);
    const [files, setFiles] = useState([]);

    const ExecAdminGetStructuredHtmlApi = AdminGetStructuredHtmlApi();
    const ExecAdminGetStructuredDocumentApi = AdminGetStructuredDocumentApi();

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);
    
    useEffect(() => {
        if(initializeState !== Constants.INITIALIZE_STATE.allComplete)
        {
            return;
        }

        // 現在の設定で初期表示設定を行う
        setLearningUrl("");
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));

    }, [initializeState]);

    // ページ読み込み実行処理
    const AdminGetStructuredHtmlApiRequest = async (url) => {
        // Webページ取得API

        // 通信中の表示を出す
        dispatch(setLoading(true));

        const requestParams = {
            url: url
        };

        const result = await ExecAdminGetStructuredHtmlApi(requestParams);

        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return null;
        }
        else
        {
            return result.response.page;
        }
    };

    const AdminGetStructuredDocumentApiRequest = async (file, file_name) => {
        // アセットアップロードAPI
        
        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await ExecAdminGetStructuredDocumentApi(file, file_name);
        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return null;
        }
        else
        {
            return result.response.page;
        }
    };

    const ExecDownLoadHTMLAsync = () => {ExecDownLoadHTML()}
    const ExecDownLoadHTML = async () => {
        setHTMLDownloadWaiting(true);
        const page = await AdminGetStructuredHtmlApiRequest(learningUrl)
        if (page === null) {
            setHTMLDownloadWaiting(false);
            return;
        }
        
        // テキストをBlob化する（MIMEタイプは 'text/plain'）
        const blob = new Blob([page], { type: 'text/plain' });

        // Blob URLを作成
        const blobUrl = window.URL.createObjectURL(blob);
        
        // ダウンロードリンクを生成
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = convertUrlToFileName(learningUrl) + '.txt'; // ダウンロードするファイル名を指定
        link.click();

        // 一時的なBlob URLを解放
        window.URL.revokeObjectURL(blobUrl);
        setHTMLDownloadWaiting(false);
    }

    const ExecDownLoadDocumentAsync = () => {ExecDownLoadDocument()}
    const ExecDownLoadDocument = async () => {

        if (!files || files.length == 0)
        {
            return
        }
        setDocumentDownloadWaiting(true);

        const page = await AdminGetStructuredDocumentApiRequest(files[0].file, files[0].file.name)
        if (page === null) {
            setDocumentDownloadWaiting(false);
            return;
        }
        
        // テキストをBlob化する（MIMEタイプは 'text/plain'）
        const blob = new Blob([page], { type: 'text/plain' });

        // Blob URLを作成
        const blobUrl = window.URL.createObjectURL(blob);

        // ファイル名を調整
        let fileName = convertDownLoadFileName(files[0].file.name);
        
        // ダウンロードリンクを生成
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName + '.txt'; // ダウンロードするファイル名を指定
        link.click();

        // 一時的なBlob URLを解放
        window.URL.revokeObjectURL(blobUrl);
        setDocumentDownloadWaiting(false);
    }

    const convertUrlToFileName = (url) => {
        // 1. https://を削除
        let fileName = url.replace(/^https?:\/\//, '');
    
        // 2. .と/をアンダーバーに変換
        fileName = fileName.replace(/[./]/g, '_');
    
        // 3. 末尾のアンダーバーを削除
        fileName = fileName.replace(/_+$/, '');
    
        // 4. 現在の日時を取得 (yyyyMMdd_HHmmss 形式)
        const formattedDate = getFormattedDate()
    
        // 5. 末尾に現在の日時を追加
        fileName += `_${formattedDate}`;
    
        return fileName;
    }

    const convertDownLoadFileName = (originFileName) => {
        // 2. .と/をアンダーバーに変換
        let fileName = originFileName.replace(/[./]/g, '_');
    
        // 3. 末尾のアンダーバーを削除
        fileName = fileName.replace(/_+$/, '');
    
        // 4. 現在の日時を取得 (yyyyMMdd_HHmmss 形式)
        const formattedDate = getFormattedDate()
    
        // 5. 末尾に現在の日時を追加
        fileName += `_${formattedDate}`;
    
        return fileName;
    }

    const getFormattedDate = () => {
        // 4. 現在の日時を取得 (yyyyMMdd_HHmmss 形式)
        const now = new Date();
        const formattedDate = now.getFullYear().toString() +
            String(now.getMonth() + 1).padStart(2, '0') +
            String(now.getDate()).padStart(2, '0') + '_' +
            String(now.getHours()).padStart(2, '0') +
            String(now.getMinutes()).padStart(2, '0') +
            String(now.getSeconds()).padStart(2, '0');

        return formattedDate
    }

    return (
    <PageContent pageId="AdminUserRagStructure">
            <PageHeader>
                <Stack className="spacing-4px">
                    <div className="page-header-title">データ構造化</div>
                    <div className="page-header-detail">各種データをRAGで使用する形式に変換します。</div>
                </Stack>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    {/* ここにページ内コンテンツのコンポーネントを記述 */}
                    <div className="block-center">
                        <Card className="base-card">
                            <Stack className="flex-start spacing-32px align-center width-full">
                                <Stack className= "input-row-area align-center spacing-16px width-full">
                                    <Stack className="align-center flex-center spacing-16px width-full">
                                        <Stack className="flex-left width-full">
                                            <div className="text-size-medium text-bold">URLから構造化</div>
                                        </Stack>
                                        <TextInput 
                                            default={learningUrl}
                                            onchange={(e) => setLearningUrl(e.target.value)} 
                                            placeholder="URLを入力" 
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                        />
                                        <InfoCard className="input-form-area">
                                                <div>情報を参照するページのURLを入力してください。</div>
                                                <div>認証情報がある場合、認証パラメータを含めたURLを入力してください。</div>
                                        </InfoCard>
                                        <Row className="flex-center width-full">
                                            <Button className="primary width-320px" onClick={() => {ExecDownLoadHTMLAsync()}} disabled={! (learningUrl && learningUrl !== "")}>
                                                {htmlDownloadWaiting ? 
                                                    <Stack className="flex-center align-center">
                                                        <LoadingBox></LoadingBox>
                                                    </Stack>
                                                    :
                                                    <Row className="flex-center spacing-8px width-full">
                                                        <Icon className="label-icon" icon="download" size={18} />
                                                        <div>変換</div>
                                                    </Row>
                                                }
                                            </Button>
                                        </Row>
                                    </Stack>
                                </Stack>
                                <hr/>
                                <Stack className= "flex-center align-center spacing-16px width-full">
                                    <Stack className="flex-center align-center spacing-16px width-full">
                                        <Stack className="flex-center width-full">
                                            <div className="text-size-medium text-bold">ファイルから構造化</div>
                                        </Stack>
                                        <Uploader
                                            className="custom-uploader"
                                            accept={Constants.ACCEPT_DOC_EXTENSION_LIST}
                                            onFileChanged={(files) => {
                                                console.log(files);
                                                setFiles(files)
                                            }}
                                            files={files}
                                            isSwap={true}
                                            minimum={false}
                                        />
                                    </Stack>
                                    <Stack className="align-center">
                                        <Button className="primary width-320px" onClick={() => {ExecDownLoadDocumentAsync()}} disabled={! (files && files.length > 0)}>
                                            {documentDownloadWaiting ? 
                                                <Stack className="flex-center align-center">
                                                    <LoadingBox></LoadingBox>
                                                </Stack>
                                                :
                                                <Row className="flex-center spacing-8px width-full">
                                                    <Icon className="label-icon" icon="download" size={18} />
                                                    <div>変換</div>
                                                </Row>
                                            }
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Card>
                    </div>
                </PageContainer>
            </PageWrapper>
    </PageContent>
    );
};

export default AdminUserRagStructure;